<template>
  <modal
    :name="name"
    :width="960"
    :height="720"
    :adaptive="true"
    :scrollable="false"
    class="support-image-gallery-modal modal-not-overflow"
    @opened="opened"
    @closed="closed"
  >
    <section
      v-if="activeImage"
      ref="gallery"
      class="support-image-gallery"
      tabindex="-1"
      :aria-label="`Image preview: ${activeImage.name}`"
    >
      <header>
        <div>
          <strong>{{ activeImage.name }}</strong>
          <span>{{ activeIndex + 1 }} of {{ images.length }}</span>
        </div>
        <app-modal-close design="new" label="Close image preview" @click="$modal.hide(name)" />
      </header>

      <div class="support-image-gallery__stage">
        <button
          v-if="images.length > 1"
          type="button"
          class="support-image-gallery__arrow support-image-gallery__arrow--previous"
          aria-label="Previous image"
          @click="previous"
        >‹</button>
        <img :src="activeImage.url" :alt="activeImage.name">
        <button
          v-if="images.length > 1"
          type="button"
          class="support-image-gallery__arrow support-image-gallery__arrow--next"
          aria-label="Next image"
          @click="next"
        >›</button>
      </div>

      <footer v-if="images.length > 1">
        <button
          v-for="(image, index) in images"
          :key="image.key"
          type="button"
          :class="{ 'support-image-gallery__thumbnail--active': index === activeIndex }"
          :aria-label="`Show ${image.name}`"
          @click="setIndex(index)"
        >
          <img :src="image.url" :alt="image.name">
        </button>
      </footer>
    </section>
  </modal>
</template>

<script>
import AppModalClose from '@/components/AppModalClose'

export default {
  name: 'SupportImageGalleryModal',
  components: { AppModalClose },
  props: {
    name: { type: String, required: true },
    images: { type: Array, default: () => [] },
    initialIndex: { type: Number, default: 0 }
  },
  data() {
    return {
      activeIndex: 0,
      visible: false
    }
  },
  computed: {
    activeImage() { return this.images[this.activeIndex] || null }
  },
  watch: {
    initialIndex: {
      immediate: true,
      handler(index) { this.setIndex(index) }
    },
    images() { this.setIndex(this.activeIndex) }
  },
  mounted() { document.addEventListener('keydown', this.handleDocumentKeydown) },
  beforeDestroy() { document.removeEventListener('keydown', this.handleDocumentKeydown) },
  methods: {
    setIndex(index) {
      if (!this.images.length) {
        this.activeIndex = 0
        return
      }
      this.activeIndex = Math.min(Math.max(Number(index) || 0, 0), this.images.length - 1)
    },
    previous() {
      if (this.images.length > 1) this.activeIndex = (this.activeIndex - 1 + this.images.length) % this.images.length
    },
    next() {
      if (this.images.length > 1) this.activeIndex = (this.activeIndex + 1) % this.images.length
    },
    opened() {
      this.visible = true
      this.setIndex(this.initialIndex)
      this.$nextTick(() => this.$refs.gallery?.focus())
    },
    closed() { this.visible = false },
    handleDocumentKeydown(event) {
      if (!this.visible) return
      if (event.key === 'ArrowLeft') this.previous()
      if (event.key === 'ArrowRight') this.next()
    }
  }
}
</script>

<style lang="scss">
.support-image-gallery-modal > .vm--overlay { background: rgba(15, 23, 42, .58); }
.support-image-gallery-modal > .vm--modal { height: 720px !important; max-height: calc(100vh - 32px) !important; overflow: hidden !important; border: 1px solid $ui-line; border-radius: $ui-overlay-radius !important; background: $ui-surface; box-shadow: 0 24px 70px rgba(15, 23, 42, .28) !important; }
.support-image-gallery { display: grid; height: 100%; min-height: 0; grid-template-rows: auto minmax(0, 1fr) auto; outline: 0; background: $ui-surface; color: $ui-ink; font-family: $ui-font-body; }
.support-image-gallery > header { display: flex; min-height: 64px; align-items: center; justify-content: space-between; gap: 18px; padding: 12px 16px 12px 20px; border-bottom: 1px solid $ui-line; background: $ui-surface; }
.support-image-gallery > header div { display: grid; min-width: 0; gap: 3px; }
.support-image-gallery > header strong { overflow: hidden; font-size: 15px; text-overflow: ellipsis; white-space: nowrap; }
.support-image-gallery > header div > span { color: $ui-muted; font-size: 12px; }
.support-image-gallery__stage { position: relative; display: flex; min-width: 0; min-height: 0; align-items: center; justify-content: center; overflow: hidden; padding: 20px 76px; background: $ui-surface-soft; }
.support-image-gallery__stage > img { display: block; width: 100%; height: 100%; border-radius: 8px; object-fit: contain; filter: drop-shadow(0 12px 24px rgba(0, 0, 0, .28)); }
.support-image-gallery__arrow { position: absolute; z-index: 1; top: 50%; display: grid; width: 44px; height: 54px; place-items: center; padding: 0 0 5px; transform: translateY(-50%); border: 1px solid $ui-line-strong; border-radius: 12px; background: rgba(255, 255, 255, .94); color: $ui-ink; cursor: pointer; font-size: 36px; line-height: 1; box-shadow: 0 8px 24px rgba(28, 45, 74, .14); }
.support-image-gallery__arrow:hover { border-color: $ui-blue; background: $ui-blue; color: #fff; }
.support-image-gallery__arrow--previous { left: 18px; }
.support-image-gallery__arrow--next { right: 18px; }
.support-image-gallery > footer { display: flex; min-height: 76px; align-items: center; justify-content: center; gap: 8px; overflow-x: auto; padding: 10px 18px; border-top: 1px solid $ui-line; background: $ui-surface; scrollbar-gutter: stable; }
.support-image-gallery > footer button { width: 54px; height: 54px; flex: 0 0 54px; overflow: hidden; padding: 0; border: 2px solid transparent; border-radius: 8px; background: transparent; cursor: pointer; opacity: .58; }
.support-image-gallery > footer button:hover,
.support-image-gallery > footer .support-image-gallery__thumbnail--active { border-color: $ui-blue; opacity: 1; }
.support-image-gallery > footer img { width: 100%; height: 100%; object-fit: cover; }
@media (max-width: 680px) {
  .support-image-gallery-modal > .vm--modal { height: calc(100vh - 20px) !important; max-height: calc(100vh - 20px) !important; }
  .support-image-gallery__stage { padding: 12px 52px; }
  .support-image-gallery__arrow { width: 38px; height: 48px; }
  .support-image-gallery__arrow--previous { left: 8px; }
  .support-image-gallery__arrow--next { right: 8px; }
}
</style>
